import React, { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import AssamMap from "../AssamMap";
import { DISTRICTS, FACILITIES, SKU_LIST } from "../../data/dashboardData";
import { hBarOptsNoMax, sortBtn } from "../../utils/chartHelpers";
import CardHead from "../../utils/CardHead";

export default function TabNearExpiry() {
  const [expSort, setExpSort] = useState("desc");

  const sortedExpiry = useMemo(
    () =>
      [...DISTRICTS]
        .sort((a, b) =>
          expSort === "desc" ? b.expQty - a.expQty : a.expQty - b.expQty,
        )
        .slice(0, 10),
    [expSort],
  );

  // const expiryChartData = {
  //   labels: sortedExpiry.map((d) => d.n),
  //   datasets: [
  //     {
  //       label: "Near Expiry SKUs",
  //       data: sortedExpiry.map((d) => d.expSkus),
  //       backgroundColor: "#fbbf24",
  //       borderRadius: 4,
  //       barThickness: 10,
  //     },
  //   ],
  // };

  const facilitywiseNearExpirydata = {
    labels: FACILITIES.map((f) => f.name),
    datasets: [
      {
        label: "Quantity",
        data: FACILITIES.map((f) => f.expiryQty),
        backgroundColor: "#d97706",
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        datalabels: {
          display: true,
          anchor: "end",
          align: "top",
          offset: 6,
          color: "#92400e",
          font: { size: 9, weight: "700" },
          formatter: function (v) {
            return v.toLocaleString();
          },
        },
      },
      {
        label: "Value (₹)",
        data: FACILITIES.map((f) => f.expiryValue),
        backgroundColor: "#7c3aed",
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        datalabels: {
          display: true,
          anchor: "end",
          align: "right",
          color: "#5b21b6",
          font: { size: 9, weight: "700" },
          formatter: function (v) {
            return "₹" + (v / 1000).toFixed(0) + "K";
          },
        },
      },
    ],
  };

  const expiryChartData = {
    labels: sortedExpiry.map((d) => d.n),
    datasets: [
      {
        label: "Quantity",
        data: sortedExpiry.map((d) => d.expQty),
        backgroundColor: "#d97706",
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        xAxisID: "x",
        datalabels: {
          display: true,
          anchor: "end",
          align: "right",
          color: "#92400e",
          font: { size: 9, weight: "700" },
          formatter: function (v) {
            return v.toLocaleString();
          },
        },
      },
      {
        label: "Value (₹)",
        data: sortedExpiry.map((d) => d.expValue),
        backgroundColor: "#7c3aed",
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        xAxisID: "x1",
        datalabels: {
          display: true,
          anchor: "end",
          align: "right",
          color: "#5b21b6",
          font: { size: 9, weight: "700" },
          formatter: function (v) {
            return "₹" + (v / 1000).toFixed(0) + "K";
          },
        },
      },
    ],
  };
  const options = {
    ...hBarOptsNoMax(true),

    indexAxis: "y",

    layout: {
      padding: {
        right: 40, // prevents ₹ label cut
      },
    },

    plugins: {
      datalabels: {
        clip: false, // allow outside labels
      },
      legend: {
        position: "top",
      },
    },

    scales: {
      x: {
        ticks: {
          callback: (v) => v.toLocaleString(),
        },
      },
      x1: {
        display: false,
      },
      y: {
        grid: { display: false },
      },
    },
  };

  return (
    <div className={`viz-pane active`}>
      <div className="chart-grid-3" style={{ alignItems: "stretch" }}>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-map-marked-alt"
            title="📍 Near Expiry Heat Map"
            subtitle="Districts by number of near-expiry SKUs"
          />
          <div className="card-body" style={{ flex: 1, padding: 10 }}>
            <AssamMap mode="expiry" />
          </div>
        </div>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-chart-bar"
            title="📊 District-wise Near Expiry (Qty vs Value)"
            subtitle={
              <>
                Top 10 | {expSort === "desc" ? "Descending" : "Ascending"}{" "}
                <span style={{ display: "flex", gap: 2 }}>
                  {sortBtn(
                    expSort === "desc",
                    () => setExpSort("desc"),
                    "\u25BC",
                  )}
                  {sortBtn(
                    expSort === "asc",
                    () => setExpSort("asc"),
                    "\u25B2",
                  )}
                </span>
              </>
            }
          />
          <div className="card-body" style={{ flex: 1, minHeight: 300 }}>
            <Bar data={expiryChartData} options={options} />
          </div>
        </div>
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <CardHead
            icon="fas fa-hospital"
            title="🏥 Facility-wise Near Expiry (Qty vs Value)"
            subtitle="By facility type"
          />
          <div className="card-body" style={{ flex: 1, minHeight: 300 }}>
            <Bar data={facilitywiseNearExpirydata} options={options} />
          </div>
        </div>
      </div>

      {/* District & SKU Tables */}
      <div className="chart-grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <CardHead
            icon="fas fa-table"
            title="📋 District-wise SKU vs Quantity"
            subtitle="Near expiry within 3 months — all districts"
          />
          <div
            className="card-body"
            style={{ overflowX: "auto", overflowY: "scroll", height: "400px" }}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>District</th>
                  <th>Near Expiry SKUs</th>
                  <th>Quantity</th>
                  <th>Risk Level</th>
                </tr>
              </thead>
              <tbody>
                {[...DISTRICTS]
                  .sort((a, b) => b.expSkus - a.expSkus)
                  .map((d, i) => (
                    <tr key={i}>
                      <td style={{ color: "var(--muted)", fontSize: ".65rem" }}>
                        {i + 1}
                      </td>
                      <td>
                        <strong>{d.n}</strong>
                      </td>
                      <td>
                        <span className="tbl-chip chip-amber">
                          {d.expSkus} SKUs
                        </span>
                      </td>
                      <td>{d.expQty.toLocaleString()}</td>
                      <td>
                        <span
                          className={`tbl-chip ${d.expSkus >= 10 ? "chip-red" : d.expSkus >= 6 ? "chip-amber" : "chip-green"}`}
                        >
                          {d.expSkus >= 10
                            ? "High"
                            : d.expSkus >= 6
                              ? "Medium"
                              : "Low"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <CardHead
            icon="fas fa-clock"
            title="⏳ Near Expiry Medicines — SKU vs Quantity "
            subtitle="Within 3 months | 50 SKUs | 10,000 units at risk"
          />
          <div
            className="card-body"
            style={{ overflowX: "auto", overflowY: "scroll", height: "400px" }}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th>Medicine</th>
                  <th>Category</th>
                  <th>Expiry</th>
                  <th>Facilities</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {SKU_LIST.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <strong>{s.name}</strong>
                    </td>
                    <td>
                      <span className="tbl-chip chip-blue">{s.cat}</span>
                    </td>
                    <td style={{ color: "var(--red)", fontWeight: 700 }}>
                      {s.expDate}
                    </td>
                    <td>{s.facilities}</td>
                    <td>{s.qty.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
